import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import {createUUID, extractErrorMessage, goBack} from 'src/app/common/common';
import { PowerTypes, siScaleCodes } from 'src/app/model/powerTypes.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {HttpParams} from "@angular/common/http";

@Component({
  selector: 'app-create-powertypes',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './create-powertypes.component.html',
  styleUrl: './create-powertypes.component.scss'
})
export class CreatePowertypesComponent implements OnInit, OnDestroy {

  //form
  powerTypeForm = new FormGroup({
    itemLabel: new FormControl<string>('', Validators.required),
    itemUnit: new FormControl<string>('', Validators.required),
    siScaleCode: new FormControl<string>(''),
    hertz: new FormControl<number | null>(null, Validators.required),
    voltage: new FormControl<number | null>(null, Validators.required),
    ac: new FormControl<boolean>(false),
  });

  unsubscribe = new Subject<void>();
  formsSubscribe: any;

  public editing: boolean = false;
  public error: string | null = null;
  public powertypeId: string = '';
  public router: Router;
  public item_units: string[] = [];
  public powertypeDescriptions: string[] = [];

  public powertype: PowerTypes = {
    itemLabel: '',
    itemUnit: '',
    siScaleCode: '',
    hertz: 0,
    voltage: 0,
    ac: false,
    updated_by: '',
    itemDescription: ''
  };

  public si_scale_codes: siScaleCodes[] = [{
    documentation: '',
    value: '',
    siScaleCode: ''
  }];

  constructor(private injector: Injector, private createPowerTypes: ApiService) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.powertypeId = event.snapshot.params.id;
      }
    });
  }

  setFormsDefaultValue() {
    this.powerTypeForm.controls.itemLabel.setValue(this.powertype.itemLabel);
    this.powerTypeForm.controls.itemUnit.setValue(this.powertype.itemUnit);
    this.powerTypeForm.controls.siScaleCode.setValue(this.powertype.siScaleCode);
    this.powerTypeForm.controls.hertz.setValue(Number(this.powertype.hertz));
    this.powerTypeForm.controls.voltage.setValue(Number(this.powertype.voltage));
    this.powerTypeForm.controls.ac.setValue(this.powertype.ac);
  }

  getAll() {
    forkJoin([
      this.createPowerTypes.getPowertypesDescriptions().pipe(takeUntil(this.unsubscribe)),
      this.createPowerTypes.getSiScaleCode().pipe(takeUntil(this.unsubscribe)),
    ]).subscribe({
      next: ([powerDescription, siScaleCode]) => {
        if (powerDescription) {
          this.powertypeDescriptions = powerDescription;
        }
        if (siScaleCode) {
          this.si_scale_codes = siScaleCode;
        }

        if (this.editing) {
          const queryParams: CustomHttpParams = {
            'depth': 1,
            'itemDescription': this.powertypeId
          };
          this.createPowerTypes.getPowerType(queryParams)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: (response: any) => {
                if (response && response.length) {
                  this.powertype = response[0];
                  this.useItemDescription(this.powertype.itemLabel);
                  this.setFormsDefaultValue();
                }
              },
              error: (err) => {
                console.error(err);
                this.error = "Failed to load powertype data.";
              }
            });
        } else {
          // For new powertype, create a UUID
          this.powertype.itemDescription = createUUID();
          this.powertype.siScaleCode = '';
          this.powerTypeForm.controls.siScaleCode.setValue('');
        }
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err, "Failed to load required data.");
      }
    });
  }

  ngOnInit(): void {
    this.getAll();

    this.setupFormSubscriptions();
  }

  setupFormSubscriptions() {
    this.formsSubscribe = this.powerTypeForm.controls.itemLabel.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val) {
          this.powertype.itemLabel = val;
          this.useItemDescription(val);
        }
      });

    this.formsSubscribe = this.powerTypeForm.controls.itemUnit.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val) this.powertype.itemUnit = val;
      });

    this.formsSubscribe = this.powerTypeForm.controls.siScaleCode.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        // Allow empty string as valid value (Native Scale)
        if (val === null || val === undefined) {
          this.powertype.siScaleCode = '';
        } else {
          this.powertype.siScaleCode = val;
        }
      });

    this.formsSubscribe = this.powerTypeForm.controls.hertz.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) this.powertype.hertz = Number(val);
      });

    this.formsSubscribe = this.powerTypeForm.controls.voltage.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) this.powertype.voltage = Number(val);
      });

    this.formsSubscribe = this.powerTypeForm.controls.ac.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        this.powertype.ac = !!val;
      });
  }

  useItemDescription(itemLabel: string) {
    this.createPowerTypes.getItemUnits(itemLabel)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (itemUnits) => {
          if (itemUnits) {
            this.item_units = itemUnits;

            const currentUnitStillValid = this.item_units.includes(this.powertype.itemUnit);
            if (!currentUnitStillValid) {
              if (this.item_units.length === 1) {
                this.powertype.itemUnit = this.item_units[0];
                this.powerTypeForm.controls.itemUnit.setValue(this.item_units[0]);
              } else {
                this.powertype.itemUnit = '';
                this.powerTypeForm.controls.itemUnit.setValue('');
              }
            }
          }
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err,"Failed to load item units.");
        }
      });
  }

  save() {
    if (this.powerTypeForm.invalid) {
      this.error = 'Please fill out all fields correctly and try again.';
      return;
    }

    if (this.editing) {
      this.createPowerTypes.editPowerType(this.powertype).subscribe({
        next: () => {
          goBack('/powertypes', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err, "Failed to update powertype.");
        }
      });
    } else {
      this.createPowerTypes.createPowerType(this.powertype).subscribe({
        next: () => {
          goBack('/powertypes', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err, "Failed to create powertype.");
        }
      });
    }
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
